/**
 * UserPreferencesForm Component
 *
 * Form for managing user preferences like timezone, date format, notifications
 */

import React from 'react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { UserPreferences } from '@/features/settings/types/settings.types';
import { TIMEZONES } from '@/components/connectors/schedule/timezones';

export interface UserPreferencesFormProps {
  preferences: UserPreferences;
  onChange: (preferences: Partial<UserPreferences>) => void;
}

export const UserPreferencesForm: React.FC<UserPreferencesFormProps> = ({
  preferences,
  onChange,
}) => {
  const dateFormats = [
    'MM/DD/YYYY',
    'DD/MM/YYYY',
    'YYYY-MM-DD',
    'DD-MMM-YYYY',
  ];

  const languages = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Spanish' },
    { value: 'fr', label: 'French' },
    { value: 'de', label: 'German' },
    { value: 'ja', label: 'Japanese' },
    { value: 'zh', label: 'Chinese' },
  ];

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Regional Settings</h3>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="language">Language</Label>
            <Select
              value={preferences.language}
              onValueChange={(value) => onChange({ language: value })}
            >
              <SelectTrigger id="language">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {languages.map((lang) => (
                  <SelectItem key={lang.value} value={lang.value}>
                    {lang.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="timezone">Timezone</Label>
            <Select
              value={preferences.timezone}
              onValueChange={(value) => onChange({ timezone: value })}
            >
              <SelectTrigger id="timezone">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONES.map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="date-format">Date Format</Label>
            <Select
              value={preferences.dateFormat}
              onValueChange={(value) => onChange({ dateFormat: value })}
            >
              <SelectTrigger id="date-format">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {dateFormats.map((format) => (
                  <SelectItem key={format} value={format}>
                    {format}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="time-format">Time Format</Label>
            <Select
              value={preferences.timeFormat}
              onValueChange={(value) =>
                onChange({ timeFormat: value as '12h' | '24h' })
              }
            >
              <SelectTrigger id="time-format">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="12h">12-hour</SelectItem>
                <SelectItem value="24h">24-hour</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-medium">Notifications</h3>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="email-notifications">Email Notifications</Label>
              <p className="text-sm text-muted-foreground">
                Receive notifications via email
              </p>
            </div>
            <Switch
              id="email-notifications"
              checked={preferences.notifications.email}
              onCheckedChange={(checked) =>
                onChange({
                  notifications: {
                    ...preferences.notifications,
                    email: checked,
                  },
                })
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="browser-notifications">Browser Notifications</Label>
              <p className="text-sm text-muted-foreground">
                Receive browser push notifications
              </p>
            </div>
            <Switch
              id="browser-notifications"
              checked={preferences.notifications.browser}
              onCheckedChange={(checked) =>
                onChange({
                  notifications: {
                    ...preferences.notifications,
                    browser: checked,
                  },
                })
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="upload-complete">Upload Complete</Label>
              <p className="text-sm text-muted-foreground">
                Notify when file uploads are complete
              </p>
            </div>
            <Switch
              id="upload-complete"
              checked={preferences.notifications.uploadComplete}
              onCheckedChange={(checked) =>
                onChange({
                  notifications: {
                    ...preferences.notifications,
                    uploadComplete: checked,
                  },
                })
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="schema-validation">Schema Validation</Label>
              <p className="text-sm text-muted-foreground">
                Notify when schema validation fails
              </p>
            </div>
            <Switch
              id="schema-validation"
              checked={preferences.notifications.schemaValidation}
              onCheckedChange={(checked) =>
                onChange({
                  notifications: {
                    ...preferences.notifications,
                    schemaValidation: checked,
                  },
                })
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="monitor-alerts">Directory Monitor Alerts</Label>
              <p className="text-sm text-muted-foreground">
                Notify when directory monitors detect new files
              </p>
            </div>
            <Switch
              id="monitor-alerts"
              checked={preferences.notifications.monitorAlerts}
              onCheckedChange={(checked) =>
                onChange({
                  notifications: {
                    ...preferences.notifications,
                    monitorAlerts: checked,
                  },
                })
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};
